import React, { ReactElement, ForwardedRef } from 'react';
import TextFieldInternal from '@mui/material/TextField';
import RequiredMark from '../required-mark/required-mark.component';
import TextBlock from '../text-block/text-block.component';
import FormatService from '../../services/format-service';
import useStylesFieldText from './field-text.styles';
import { DATA_REF_NAME } from '../../constants/system/data-attributes';

const FieldText: ReactMemoComponent = React.memo(React.forwardRef((
  props: ReactComponentProps,
  ref: ForwardedRef<HTMLDivElement>,
): ReactElement => {
  const VARIANT_OUTLINED: TextFieldVariant = 'outlined';
  const INPUT_TYPE_TEXT: string = 'text';
  const AUTOCOMPLETE_ON: string = 'on';
  const AUTOCOMPLETE_OFF: string = 'off';
  const AUTOCOMPLETE_USERNAME: string = 'username';
  const ID_PREFIX_TEXT_INPUT: string = 'text-input';
  const COMPONENT_NAME_TEXTAREA: string = 'textarea';

  const {
    isLight,
    isPlain,
    isHidden,
    isLimited,
    isDisabled,
    isOutlined,
    isRequired,
    isBordered,
    isBold,
    isInline,
    isFullWidth,
    isMultiline,
    withNoId,
    withAutoFocus,
    withUnderline,
    withAutocomplete,
    withNoAutocomplete,
    withAutocompleteUserName,
    minRows,
    maxRows,
    maxLength,
    inputRef,
    inputRefs,
    inputRefIndex,
    label,
    value,
    id,
    icon,
    placeholder,
    className,
    onChange,
    onKeyDown,
    onKeyUp,
    onBlur,
    onPointerDown,
    onClick,
    input,
    meta,
    ...custom
  } = props;

  const {
    touched: isTouched,
    invalid: isInvalid,
    error: formError,
    submitError: formSubmitError,
    modifiedSinceLastSubmit,
  } = (meta || {}) as FormFieldMetaProps;

  const { classes } = useStylesFieldText();

  const withErrors: boolean = ((isTouched && isInvalid) || !!formError || !!formSubmitError)
    && !modifiedSinceLastSubmit;

  const borderedClassName: string | undefined = isBordered ? classes.fieldTextBordered : undefined;
  const outlinedClassName: string | undefined = isOutlined ? classes.fieldTextOutlined : undefined;
  const limitedClassName: string | undefined = isLimited ? classes.fieldTextLimited : undefined;
  const boldClassName: string | undefined = isBold ? classes.fieldTextBold : undefined;
  const noUnderlineClassName: string | undefined = !withUnderline ? classes.fieldTextNoUnderline : undefined;
  const lightClassName: string | undefined = isLight ? classes.fieldTextLight : undefined;
  const underlineClassName: string | undefined = withUnderline ? classes.fieldTextUnderline : undefined;
  const underlineLightClassName: string | undefined = withUnderline && isLight
    ? classes.fieldTextUnderlineLight : undefined;
  const hiddenClassName: string | undefined = isHidden ? classes.fieldTextHidden : undefined;
  const multilineClassName: string | undefined = isMultiline ? classes.fieldTextMultiline : undefined;

  const autoCompleteOn: string | undefined = withAutocomplete ? AUTOCOMPLETE_ON : undefined;
  const autoCompleteOff: string | undefined = withNoAutocomplete ? AUTOCOMPLETE_OFF : undefined;
  const autoCompleteUserName: string | undefined = withAutocompleteUserName ? AUTOCOMPLETE_USERNAME : undefined;
  const autoCompleteValue: string | undefined = autoCompleteOn || autoCompleteUserName || autoCompleteOff;
  const inputId: string | undefined = (!withNoId && !isPlain && input && input.name
    && `${ID_PREFIX_TEXT_INPUT}-${input.name}`) || undefined;
  const resultMinRows: number = isMultiline && isInline ? minRows : maxRows;

  return (
    <TextFieldInternal
      autoFocus={withAutoFocus}
      fullWidth={isFullWidth}
      disabled={isDisabled}
      ref={ref}
      label={(
        isOutlined ? (
          <TextBlock>
            <TextBlock>
              {label}
            </TextBlock>
            {
              isRequired && (
                <RequiredMark />
              )
            }
          </TextBlock>
        ) : label
      )}
      id={id}
      value={isPlain ? value : undefined}
      multiline={isMultiline}
      placeholder={placeholder || label}
      variant={isOutlined ? VARIANT_OUTLINED : undefined}
      error={!isPlain && withErrors}
      helperText={!isPlain && withErrors && (formError || formSubmitError)}
      minRows={minRows}
      maxRows={maxRows}
      type={INPUT_TYPE_TEXT}
      inputProps={{
        id: inputId,
        maxLength: !isMultiline ? (maxLength || FormatService.STRING_LIMIT_DEFAULT) : undefined,
        [DATA_REF_NAME]: inputRefIndex,
      }}
      inputRef={inputRef || (
        inputRefs ? (ref: HTMLInputElement | null): void => {
          inputRefs.current[inputRefIndex] = ref;
        } : null
      )}
      InputProps={{
        endAdornment: icon || undefined,
        multiline: isMultiline,
        rows: isMultiline && resultMinRows ? resultMinRows : undefined,
        inputComponent: isMultiline ? COMPONENT_NAME_TEXTAREA : undefined,
      }}
      InputLabelProps={isOutlined ? {
        htmlFor: inputId,
      } : {}}
      className={FormatService.formatClassNames(
        classes.fieldText, borderedClassName, outlinedClassName, limitedClassName, boldClassName, noUnderlineClassName,
        lightClassName, underlineClassName, underlineLightClassName, hiddenClassName, multilineClassName, className,
      )}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      onBlur={onBlur}
      onPointerDown={onPointerDown}
      onClick={onClick}
      onChange={(event: React.ChangeEvent): void => {
        if (onChange) {
          onChange(event, (event.target as HTMLInputElement).value);
        }
      }}
      {
        ...(autoCompleteValue ? {
          autoComplete: autoCompleteValue,
        } : {})
      }
      {...(!isPlain ? input : {})}
      {...custom}
    />
  );
}));

export default FieldText;
